import {
  Component,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  Input,
  HostListener,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { WIDGET_LAYOUT } from '../../../../shared/interfaces/widgetLayout';
import { TOOLTIPS_NT } from '../../../../shared/data/tooltipsNT.data';

type WidgetFilter = 'all' | 'available' | 'inUse';

// UPDATE IT EVERY TIME WE CREATE A NEW WIDGET, otherwise it is shown at the end (id 11 disabled)
const WIDGETS_ORDER = [5, 15, 1, 3, 16, 4, 21, 23, 6, 20, 10, 22, 17, 18, 19, 14, 7, 8, 12, 2, 13, 9, 0];

// widgets that take 2 columns in the grid: add here every new horizontal widget
const WIDE_WIDGETS = [
  'widget-attivita',
  'widget-curva-accesso',
  'widget-curva-mercato',
  'widget-curva-mercato-30-giorni',
  'widget-curva-mercato-30-giorni-merged',
  'widget-curva-economia',
  'widget-evolution-regional-access',
  'widget-evolution-regional-economia',
  'widget-evolution-regional-market',
  'widget-evolution-regional-market-30-days',
  'widget-inclusion-process-tool-national',
];

// "Esperienze per Regione" map: moved to the first row when there is a free slot
const ESPERIENZE_MAP_ID = 0;

// same breakpoint used in the CSS for the wide cards
const WIDE_CARDS_MIN_SCREEN_WIDTH = 640;

@Component({
  selector: 'app-add-widgets',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './add-widgets.component.html',
  styleUrl: './add-widgets.component.css',
})
export class AddWidgetsComponent implements OnInit, OnDestroy {
  @Input() allWidgets: WIDGET_LAYOUT[] = [];
  @Output() widgetsUpdated = new EventEmitter<WIDGET_LAYOUT[]>();
  @Output() addWidgetsModalOpen = new EventEmitter<boolean>();

  widgets: WIDGET_LAYOUT[] = [];
  stagedWidgets = new Map<number, boolean>();

  searchTerm: string = '';
  activeFilter: WidgetFilter = 'all';

  filterTabs: { value: WidgetFilter; label: string }[] = [
    { value: 'all', label: 'Tutti' },
    { value: 'available', label: 'Disponibili' },
    { value: 'inUse', label: 'In uso' },
  ];

  gridColumns: number = 1;
  private gridResizeObserver?: ResizeObserver;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.widgets = structuredClone(this.allWidgets);
    this.sortWidgets();
  }

  ngOnDestroy(): void {
    if (this.gridResizeObserver) {
      this.gridResizeObserver.disconnect();
    }
  }

  // the grid is under *ngIf: called every time it is created or removed
  @ViewChild('widgetsGrid')
  set widgetsGrid(gridElement: ElementRef<HTMLElement> | undefined) {
    if (this.gridResizeObserver) {
      this.gridResizeObserver.disconnect();
    }

    if (!gridElement) {
      return;
    }

    this.gridResizeObserver = new ResizeObserver(() => {
      const columns = this.countGridColumns(gridElement.nativeElement);

      if (columns !== this.gridColumns) {
        this.gridColumns = columns;
        this.cdr.detectChanges();
      }
    });

    this.gridResizeObserver.observe(gridElement.nativeElement);
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeModal();
  }

  closeModal() {
    this.addWidgetsModalOpen.emit(false);
  }

  addWidgetsToDashboard() {
    this.stagedWidgets.forEach((isSelected, widgetId) => {
      const widget = this.widgets.find((w) => w.id === widgetId);

      if (widget) {
        widget.visible = isSelected;
      }
    });

    this.widgetsUpdated.emit(this.widgets);
    this.closeModal();
  }

  toggleWidget(widget: WIDGET_LAYOUT) {
    if (widget.visible) {
      return;
    }

    const isSelectedNow = this.isSelected(widget);
    this.stagedWidgets.set(widget.id, !isSelectedNow);
  }

  isSelected(widget: WIDGET_LAYOUT): boolean {
    return this.stagedWidgets.get(widget.id) === true;
  }

  get newlySelectedCount(): number {
    let count = 0;

    for (const widget of this.widgets) {
      if (!widget.visible && this.isSelected(widget)) {
        count++;
      }
    }

    return count;
  }

  get allAvailableSelected(): boolean {
    const availableWidgets = this.getAvailableWidgets();

    if (availableWidgets.length === 0) {
      return false;
    }

    for (const widget of availableWidgets) {
      if (!this.isSelected(widget)) {
        return false;
      }
    }

    return true;
  }

  toggleSelectAllAvailable() {
    const newValue = !this.allAvailableSelected;

    for (const widget of this.getAvailableWidgets()) {
      this.stagedWidgets.set(widget.id, newValue);
    }
  }

  get availableCount(): number {
    return this.getAvailableWidgets().length;
  }

  get inUseCount(): number {
    return this.widgets.length - this.availableCount;
  }

  getFilterCount(filter: WidgetFilter): number {
    if (filter === 'available') {
      return this.availableCount;
    }

    if (filter === 'inUse') {
      return this.inUseCount;
    }

    return this.widgets.length;
  }

  setFilter(filter: WidgetFilter) {
    this.activeFilter = filter;
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
  }

  clearSearch() {
    this.searchTerm = '';
  }

  get filteredWidgets(): WIDGET_LAYOUT[] {
    const result: WIDGET_LAYOUT[] = [];

    for (const widget of this.widgets) {
      if (this.matchesFilter(widget) && this.matchesSearch(widget)) {
        result.push(widget);
      }
    }

    return result;
  }

  get displayedWidgets(): WIDGET_LAYOUT[] {
    return this.moveEsperienzeMapToFirstRow(this.filteredWidgets);
  }

  get emptyStateTitle(): string {
    if (this.searchTerm) {
      return 'Nessun widget trovato';
    }

    if (this.activeFilter === 'available') {
      return 'Tutti i widget sono già in uso';
    }

    return 'Nessun widget in uso';
  }

  get emptyStateMessage(): string {
    if (this.searchTerm) {
      return 'Prova con un termine di ricerca diverso.';
    }

    if (this.activeFilter === 'available') {
      return 'La tua dashboard contiene già tutti i widget disponibili.';
    }

    return 'Seleziona dei widget per aggiungerli alla dashboard.';
  }

  // searched by id like the dashboard: the classNames of the two data files can differ
  getTooltipText(widgetId: number): string {
    const tooltip = TOOLTIPS_NT.find((t) => t.id === widgetId);

    if (tooltip) {
      return tooltip.text;
    }

    return '';
  }

  isWide(widget: WIDGET_LAYOUT): boolean {
    return WIDE_WIDGETS.includes(widget.className);
  }

  getCardClasses(widget: WIDGET_LAYOUT): string {
    if (widget.visible) {
      return 'border-gray-100 cursor-not-allowed';
    }

    if (this.isSelected(widget)) {
      return 'border-dimot-1 shadow-md cursor-pointer';
    }

    return 'border-gray-100 hover:border-dimot-1/40 hover:shadow-md hover:-translate-y-0.5 cursor-pointer';
  }

  getPreviewClasses(widget: WIDGET_LAYOUT): string {
    if (widget.visible) {
      return widget.className + ' opacity-50 grayscale';
    }

    return widget.className + ' group-hover:scale-[1.03]';
  }

  getCheckClasses(widget: WIDGET_LAYOUT): string {
    if (this.isSelected(widget)) {
      return 'bg-dimot-1 border-dimot-1 scale-100';
    }

    return 'bg-white/90 border-gray-300 group-hover:border-dimot-1/50';
  }

  // normal cards: 2 lines of title + 4 of description, wide cards: 2 + 3
  getHoverPanelHeight(widget: WIDGET_LAYOUT): string {
    if (this.isWide(widget)) {
      return 'h-[5.9rem] 2xl:h-[6.55rem]';
    }

    return 'h-[6.8rem] 2xl:h-[7.6rem]';
  }

  getFilterTabClasses(filter: WidgetFilter): string {
    if (this.activeFilter === filter) {
      return 'bg-white text-dimot-1 shadow-sm';
    }

    return 'text-gray-500 hover:text-dimot-1';
  }

  trackById(_index: number, widget: WIDGET_LAYOUT): number {
    return widget.id;
  }

  private sortWidgets() {
    this.widgets.sort((a, b) => this.getOrderPosition(a.id) - this.getOrderPosition(b.id));
  }

  private getOrderPosition(widgetId: number): number {
    const position = WIDGETS_ORDER.indexOf(widgetId);

    if (position === -1) {
      return WIDGETS_ORDER.length;
    }

    return position;
  }

  private getAvailableWidgets(): WIDGET_LAYOUT[] {
    return this.widgets.filter((widget) => !widget.visible);
  }

  private matchesFilter(widget: WIDGET_LAYOUT): boolean {
    if (this.activeFilter === 'available') {
      return !widget.visible;
    }

    if (this.activeFilter === 'inUse') {
      return widget.visible;
    }

    return true;
  }

  private matchesSearch(widget: WIDGET_LAYOUT): boolean {
    const search = this.searchTerm.trim().toLowerCase();

    if (search === '') {
      return true;
    }

    const name = (widget.name ?? '').toLowerCase();
    const description = this.getTooltipText(widget.id).toLowerCase();

    return name.includes(search) || description.includes(search);
  }

  // e.g. "200px 200px 200px" -> 3
  private countGridColumns(grid: HTMLElement): number {
    const columnsText = getComputedStyle(grid).gridTemplateColumns;
    const columns = columnsText.split(' ').filter((value) => value !== '');

    return columns.length;
  }

  private getCardColumns(widget: WIDGET_LAYOUT): number {
    if (!this.isWide(widget)) {
      return 1;
    }

    const isBigScreen = window.innerWidth >= WIDE_CARDS_MIN_SCREEN_WIDTH;

    if (isBigScreen && this.gridColumns >= 2) {
      return 2;
    }

    return 1;
  }

  private moveEsperienzeMapToFirstRow(widgets: WIDGET_LAYOUT[]): WIDGET_LAYOUT[] {
    const mapIndex = widgets.findIndex((w) => w.id === ESPERIENZE_MAP_ID);

    if (mapIndex === -1) {
      return widgets;
    }

    // fill the first row until a card doesn't fit anymore
    let usedColumns = 0;
    let firstCardOfSecondRow = -1;

    for (let i = 0; i < widgets.length; i++) {
      if (i === mapIndex) {
        return widgets;
      }

      const cardColumns = this.getCardColumns(widgets[i]);

      if (usedColumns + cardColumns > this.gridColumns) {
        firstCardOfSecondRow = i;
        break;
      }

      usedColumns = usedColumns + cardColumns;
    }

    if (firstCardOfSecondRow === -1) {
      return widgets;
    }

    const freeColumns = this.gridColumns - usedColumns;
    const mapColumns = this.getCardColumns(widgets[mapIndex]);

    if (freeColumns < mapColumns) {
      return widgets;
    }

    const reorderedWidgets = [...widgets];
    const map = reorderedWidgets.splice(mapIndex, 1)[0];
    reorderedWidgets.splice(firstCardOfSecondRow, 0, map);

    return reorderedWidgets;
  }
}
